import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './ConnectorSettingsPage.css';

const CHANNEL_META = {
  dev: {
    label: 'Dev Channel',
    icon: '🧑‍💻',
    description: 'Developer communications — notes, bugs, progress tracking.',
    defaultLabel: 'Dev Space',
  },
  staff: {
    label: 'Staff Channel',
    icon: '👥',
    description: 'Staff collaboration and internal announcements.',
    defaultLabel: 'Staff Space',
  },
  citizen: {
    label: 'Citizen Channel',
    icon: '🏛️',
    description: 'Citizen-facing support and community communications.',
    defaultLabel: 'Citizen Space',
  },
};

const ALL_USER_TYPES = [
  { value: 'user',      label: 'User' },
  { value: 'citizen',   label: 'Citizen' },
  { value: 'staff',     label: 'Staff' },
  { value: 'education', label: 'Education' },
  { value: 'admin',     label: 'Admin' },
];

const EMPTY_CHANNELS = {
  dev:     { slug: '', label: '', enabled: false },
  staff:   { slug: '', label: '', enabled: false },
  citizen: { slug: '', label: '', enabled: false },
};

const ConnectorSettingsPage = () => {
  const { currentUser } = useContext(AuthContext);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [comsappBaseUrl, setComsappBaseUrl] = useState('');
  const [urlOverride, setUrlOverride] = useState(false);
  const [defaultComsappBaseUrl, setDefaultComsappBaseUrl] = useState('');
  const [channels, setChannels] = useState(EMPTY_CHANNELS);
  const [allowedUserTypes, setAllowedUserTypes] = useState(ALL_USER_TYPES.map((t) => t.value));

  // Per-channel slug verification state
  const [verifying, setVerifying] = useState({});
  const [verifyResult, setVerifyResult] = useState({});

  const fetchConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/connector/config');
      const cfg = res.data.config;
      setConfig(cfg);
      setDefaultComsappBaseUrl(res.data.defaultComsappBaseUrl || '');
      if (cfg) {
        setComsappBaseUrl(cfg.comsappBaseUrl || '');
        setUrlOverride(Boolean(cfg.comsappBaseUrl));
        setChannels({
          dev:     { slug: cfg.channels?.dev?.slug || '', label: cfg.channels?.dev?.label || '', enabled: cfg.channels?.dev?.enabled ?? false },
          staff:   { slug: cfg.channels?.staff?.slug || '', label: cfg.channels?.staff?.label || '', enabled: cfg.channels?.staff?.enabled ?? false },
          citizen: { slug: cfg.channels?.citizen?.slug || '', label: cfg.channels?.citizen?.label || '', enabled: cfg.channels?.citizen?.enabled ?? false },
        });
        setAllowedUserTypes(
          Array.isArray(cfg.allowedUserTypes) && cfg.allowedUserTypes.length > 0
            ? cfg.allowedUserTypes
            : ALL_USER_TYPES.map((t) => t.value)
        );
      }
    } catch (err) {
      setError('Failed to load connector config.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleChannelChange = (ch, field, value) => {
    setChannels((prev) => ({
      ...prev,
      [ch]: { ...prev[ch], [field]: value },
    }));
    // Reset verification when slug changes
    if (field === 'slug') {
      setVerifyResult((prev) => ({ ...prev, [ch]: null }));
    }
  };

  const handleVerifySlug = async (ch) => {
    const rawInput = channels[ch]?.slug?.trim();
    if (!rawInput) return;

    setVerifying((prev) => ({ ...prev, [ch]: true }));
    setVerifyResult((prev) => ({ ...prev, [ch]: null }));
    setError('');

    // Detect MongoDB ObjectId (24-char hex) — paste from URL or Space Info panel
    const isObjectId = /^[0-9a-f]{24}$/i.test(rawInput);

    try {
      const endpoint = isObjectId ? '/api/connector/verify-id' : '/api/connector/verify-slug';
      const payload = isObjectId ? { id: rawInput, channel: ch } : { slug: rawInput, channel: ch };
      const res = await api.post(endpoint, payload);
      setVerifyResult((prev) => ({ ...prev, [ch]: { ok: true, space: res.data.space } }));
      // If user pasted an ID, update the displayed slug to the resolved slug
      if (isObjectId && res.data.space?.slug) {
        setChannels((prev) => ({ ...prev, [ch]: { ...prev[ch], slug: res.data.space.slug } }));
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Verification failed.';
      setVerifyResult((prev) => ({ ...prev, [ch]: { ok: false, message: msg } }));
    } finally {
      setVerifying((prev) => ({ ...prev, [ch]: false }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/api/connector/config', { comsappBaseUrl: urlOverride ? comsappBaseUrl : '', channels, allowedUserTypes });
      setSuccess('Connector settings saved.');
      await fetchConfig();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="connector-page"><p>Loading…</p></div>;

  return (
    <div className="connector-page">
      <div className="connector-header">
        <div>
          <h1>ComSapp Connector</h1>
          <p>Link site channels to ComSapp spaces using space slugs. All communications for that channel route to the connected space.</p>
        </div>
        <Link to="/admin-dashboard" className="connector-back-link">← Admin Dashboard</Link>
      </div>

      <div className="connector-card">
        <h2>ComSapp Base URL</h2>
        <p className="connector-hint">Where ComSapp is running. Defaults to <code>{defaultComsappBaseUrl || '(not set)'}</code> — override only if you need a custom URL.</p>
        <label className="connector-toggle" style={{ marginBottom: urlOverride ? '0.75rem' : 0 }}>
          <input
            type="checkbox"
            checked={urlOverride}
            onChange={(e) => {
              setUrlOverride(e.target.checked);
              if (!e.target.checked) setComsappBaseUrl('');
            }}
          />
          <span className="connector-toggle-label">Override default URL</span>
        </label>
        {urlOverride && (
          <input
            type="text"
            className="connector-input"
            value={comsappBaseUrl}
            onChange={(e) => setComsappBaseUrl(e.target.value)}
            placeholder="https://comms.yourorg.ca"
            autoFocus
          />
        )}
        {!urlOverride && defaultComsappBaseUrl && (
          <p className="connector-default-url">Using default: <code>{defaultComsappBaseUrl}</code></p>
        )}
      </div>

      <form onSubmit={handleSave}>
        <div className="connector-card">
          <h2>ComSapp Access — User Types</h2>
          <p className="connector-hint">Select which user types are allowed to access ComSapp through this connector. Unchecked types will not be provisioned or given access.</p>
          <div className="connector-user-type-list">
            {ALL_USER_TYPES.map(({ value, label }) => (
              <label key={value} className="connector-toggle" style={{ marginBottom: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={allowedUserTypes.includes(value)}
                  onChange={(e) => {
                    setAllowedUserTypes((prev) =>
                      e.target.checked ? [...prev, value] : prev.filter((t) => t !== value)
                    );
                  }}
                />
                <span className="connector-toggle-label">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {Object.entries(CHANNEL_META).map(([ch, meta]) => {
          const result = verifyResult[ch];
          const channelCfg = config?.channels?.[ch];
          return (
            <div className="connector-card" key={ch}>
              <div className="connector-channel-header">
                <h2>{meta.icon} {meta.label}</h2>
                <label className="connector-toggle">
                  <input
                    type="checkbox"
                    checked={channels[ch]?.enabled ?? false}
                    onChange={(e) => handleChannelChange(ch, 'enabled', e.target.checked)}
                  />
                  <span className="connector-toggle-label">
                    {channels[ch]?.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
              <p className="connector-hint">{meta.description}</p>

              <div className="connector-field-row">
                <div className="connector-field">
                  <label>Space Slug or ID</label>
                  <div className="connector-slug-row">
                    <input
                      type="text"
                      className="connector-input"
                      value={channels[ch]?.slug || ''}
                      onChange={(e) => handleChannelChange(ch, 'slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      onPaste={(e) => {
                        // Allow raw paste (including ObjectIds with hex chars) before the onChange filter
                        const pasted = e.clipboardData.getData('text').trim();
                        if (/^[0-9a-f]{24}$/i.test(pasted)) {
                          e.preventDefault();
                          handleChannelChange(ch, 'slug', pasted.toLowerCase());
                        }
                      }}
                      placeholder={`slug (e.g. rs-dev) or space ID`}
                    />
                    <button
                      type="button"
                      className="connector-verify-btn"
                      disabled={!channels[ch]?.slug || verifying[ch]}
                      onClick={() => handleVerifySlug(ch)}
                    >
                      {verifying[ch] ? 'Verifying…' : 'Verify'}
                    </button>
                  </div>

                  {result?.ok && (
                    <div className="connector-verify-ok">
                      ✓ Connected to <strong>{result.space.name}</strong>
                      {result.space.isPrivate && ' (private)'}
                      {comsappBaseUrl && (
                        <a
                          href={`${comsappBaseUrl.replace(/\/$/, '')}/spaces/${result.space.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="connector-open-link"
                        >
                          Open in ComSapp
                        </a>
                      )}
                    </div>
                  )}
                  {result?.ok === false && (
                    <div className="connector-verify-fail">✗ {result.message}</div>
                  )}
                  {!result && channelCfg?.lastVerifiedAt && channelCfg?.spaceName && (
                    <div className="connector-verify-cached">
                      Last verified: <strong>{channelCfg.spaceName}</strong>{' '}
                      <span className="connector-hint-inline">
                        ({new Date(channelCfg.lastVerifiedAt).toLocaleDateString()})
                      </span>
                      {channelCfg.spaceId && (
                        <span className="connector-space-id"> · ID: <code>{channelCfg.spaceId}</code></span>
                      )}
                    </div>
                  )}
                </div>

                <div className="connector-field">
                  <label>Channel Label</label>
                  <input
                    type="text"
                    className="connector-input"
                    value={channels[ch]?.label || ''}
                    onChange={(e) => handleChannelChange(ch, 'label', e.target.value)}
                    placeholder={meta.defaultLabel}
                    maxLength={60}
                  />
                </div>
              </div>
            </div>
          );
        })}

        {error && <p className="connector-error">{error}</p>}
        {success && <p className="connector-success">{success}</p>}

        <div className="connector-actions">
          <button type="submit" className="connector-save-btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save Connector Settings'}
          </button>
        </div>
      </form>

      <div className="connector-card connector-info-card">
        <h2>How to connect an app</h2>
        <ol className="connector-steps">
          <li>Create a space in ComSapp and note its slug or ID (visible in the Space Members tab if you are owner/admin).</li>
          <li>Enter the slug (e.g. <code>rs-dev</code>) or paste the Space ID directly into the field above, then click <strong>Verify</strong>.</li>
          <li>Enable the channel and save settings.</li>
          <li>Set <code>COMSAPP_API_URL</code> and <code>COMSAPP_APP_KEY</code> in your backend <code>.env</code> file to enable live provisioning.</li>
          <li>Repeat this pattern in any connected app (CIP, KOYoga) using the same ComSapp space slugs.</li>
        </ol>
        <p className="connector-hint">Space slugs are unique across all ComSapp spaces and serve as the universal link identifier.</p>
      </div>
    </div>
  );
};

export default ConnectorSettingsPage;
